import { CalendarRegular, ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { getEndOfWeek, getStartOfWeek } from '@mnspro-cloud/services';
import moment from 'moment-timezone';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useCss } from 'react-use';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import { LocalizedDatePicker } from '../LocalizedDatePicker/LocalizedDatePicker';

export interface WeekPickerProps {
  /**
   * Used to mark the current day in the Date picker.
   * Also used as fallback for selected date if selected date not set.
   */
  today: Date;
  /**
   * Sets the selected date.
   */
  selected: Date;
  /**
   * Will be fired every time the selected date changes.
   */
  onSelected: (start: Date, end: Date) => void;
  /**
   * Disables all UI elements, if true.
   */
  disabled?: boolean;
}

const WeekPicker: FC<WeekPickerProps> = ({
  today,
  selected,
  onSelected,
  disabled,
}) => {
  const weekPickerClassName = useCss({
    div: {
      div: {
        input: {
          width: '215px',
          paddingRight: 0,
        },
      },
    },
  });

  const { i18n, t } = useTranslation();

  const formatter = (date: Date) => {
    return `${getStartOfWeek(date, i18n.language).format(
      'DD. MMMM',
    )} - ${getEndOfWeek(date, i18n.language).format('DD. MMMM')}`;
  };

  const handleSelected = (date: Date) => {
    onSelected(getStartOfWeek(date).toDate(), getEndOfWeek(date).toDate());
  };

  return (
    <div className="flex flex-wrap" data-cy="weekPicker">
      <Button
        aria-label={`${t('timetable:current_week', {
          defaultValue:
            'Aktuelle Woche: Öffnet den Stundenplan der aktuellen Woche',
        })}`}
        appearance="transparent"
        icon={<CalendarRegular />}
        onClick={() => handleSelected(new Date())}
        style={{ padding: '0', minWidth: 'auto' }}
        disabled={disabled}
      >
        {t('current_week', { defaultValue: 'Aktuelle Woche' })}
      </Button>

      <Tooltip
        trigger={
          <Button
            aria-label={t('previous_week', { defaultValue: 'Vorherige Woche' })}
            icon={<ChevronLeftRegular />}
            appearance="transparent"
            onClick={() =>
              handleSelected(moment(selected).subtract(1, 'weeks').toDate())
            }
            data-cy="btnPrevWeek"
            disabled={disabled}
          />
        }
        content={t('previous_week', { defaultValue: 'Vorherige Woche' })}
      />
      <Tooltip
        trigger={
          <Button
            aria-label={t('next_week', { defaultValue: 'Nächste Woche' })}
            icon={<ChevronRightRegular />}
            appearance="transparent"
            onClick={() =>
              handleSelected(moment(selected).add(1, 'weeks').toDate())
            }
            data-cy="btnNextWeek"
            disabled={disabled}
          />
        }
        content={t('next_week', { defaultValue: 'Nächste Woche' })}
      />
      <LocalizedDatePicker
        disabled={disabled}
        className={weekPickerClassName}
        aria-label={t('select_week', { defaultValue: 'Woche auswählen' })}
        formatMonthDayYear={formatter}
        firstDayOfWeek={1}
        allowManualInput={false}
        today={today}
        defaultSelectedDate={today}
        selectedDate={selected}
        onDateChange={(_, dateChangeArguments: any) =>
          handleSelected(dateChangeArguments.value)
        }
      />
    </div>
  );
};

export { WeekPicker };
